import { useEffect, useRef, useState } from 'react';

import { extrairSessionId } from '@/features/logout/lib/logout-token';
import { resolverDestinoPosLogout } from '@/features/logout/lib/post-logout-destino';
import { deleteSession } from '@/shared/lib/zitadel-client';
import { AuthStage } from '@/shared/ui/auth-stage';
import { Button } from '@/shared/ui/button';

const APP_URL = import.meta.env.VITE_APP_URL ?? 'http://localhost:5173';
const LOGIN_BASE_URL = import.meta.env.VITE_LOGIN_BASE_URL;

/**
 * Última etapa do logout (Login V2).
 *
 * Quando o app chama `/oidc/v1/end_session`, o Zitadel encerra a sessão e manda
 * o browser para a logout URL V2 configurada em `ZITADEL_OIDC_DEFAULTLOGOUTURLV2`
 * (`https://login.groupws.com.br/logout?post_logout_redirect=…`). Cabe a esta
 * rota fazer o salto final — sem ela o usuário parava aqui e nunca voltava ao app.
 *
 * A tela é só uma ponte: aparece por um instante e some. O texto existe para o
 * caso raro do redirect ser bloqueado (extensão, popup blocker), com um botão
 * manual para o mesmo destino.
 */
export function LogoutPage() {
  const [destino] = useState(() =>
    resolverDestinoPosLogout(
      window.location.search,
      APP_URL,
      LOGIN_BASE_URL ? [LOGIN_BASE_URL] : [],
    ),
  );
  const saltouRef = useRef(false);

  useEffect(() => {
    if (saltouRef.current) return;
    saltouRef.current = true;

    async function sair() {
      // Apaga a sessão V2 — sem isso o próximo /authorize reautentica sozinho e
      // o usuário "sai" sem nunca ver a tela de login de novo. É best-effort:
      // falhar aqui não pode prender ninguém nesta página.
      const sessionId = extrairSessionId(window.location.search);
      if (sessionId) {
        try {
          await deleteSession(sessionId);
        } catch {
          // Sessão já expirada/apagada, ou proxy indisponível — segue o redirect.
        }
      }
      // `replace` para o botão "voltar" não trazer o usuário de volta ao logout.
      window.location.replace(destino);
    }

    void sair();
  }, [destino]);

  return (
    <AuthStage
      quote={
        <>
          "Até logo.
          <br />
          <em>A porta continua aberta.</em>"
        </>
      }
      quoteCite="· Group WS Platform"
      metaLeft="Logout · sessão encerrada"
      metaRight="OIDC"
    >
      <p className="mb-[22px] font-mono text-[10px] uppercase tracking-[0.15em] text-ink-4">
        Saindo
      </p>

      <h1 className="mb-3 font-editorial text-[38px] font-light leading-[1.05] tracking-[-0.03em] text-ink-1">
        Você <em className="font-light italic text-ink-3">saiu.</em>
      </h1>
      <p className="mb-[30px] max-w-[360px] text-[14px] leading-[1.55] text-ink-3">
        Sua sessão foi encerrada. Estamos levando você de volta.
      </p>

      <div className="space-y-3.5">
        <div className="flex items-center gap-3 text-[13px] text-ink-3">
          <span className="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
          Redirecionando…
        </div>
        <Button type="button" size="lg" onClick={() => window.location.replace(destino)}>
          Continuar
        </Button>
      </div>
    </AuthStage>
  );
}
